import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AgencyUser {
  id: string;
  email: string;
  name: string;
  companyName: string | null;
  couponPrefix: string;
  agencyTier: number;
  commissionRate: string;
}

interface AgencyAuthState {
  agencyUser: AgencyUser | null;
  token: string | null;
  expiresAt: Date | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (agencyUser: AgencyUser, token: string, expiresAt: Date | string) => void;
  clearAuth: () => void;
  updateToken: (token: string, expiresAt: Date | string) => void;
}

export const useAgencyAuthStore = create<AgencyAuthState>()(
  persist(
    (set) => ({
      agencyUser: null,
      token: null,
      expiresAt: null,
      isAuthenticated: false,

      setAuth: (agencyUser, token, expiresAt) => {
        // Store token in localStorage for API calls
        localStorage.setItem('agencyToken', token);

        // Convert to ISO string if it's a Date object, otherwise use as is
        const expiryString = expiresAt instanceof Date
          ? expiresAt.toISOString()
          : expiresAt;
        localStorage.setItem('agencyTokenExpiry', expiryString);

        // Convert to Date object for state storage
        const expiryDate = expiresAt instanceof Date
          ? expiresAt
          : new Date(expiresAt);

        set({
          agencyUser,
          token,
          expiresAt: expiryDate,
          isAuthenticated: true,
        });
      },

      clearAuth: () => {
        // Remove token from localStorage
        localStorage.removeItem('agencyToken');
        localStorage.removeItem('agencyTokenExpiry');

        set({
          agencyUser: null,
          token: null,
          expiresAt: null,
          isAuthenticated: false,
        });
      },

      updateToken: (token, expiresAt) => {
        localStorage.setItem('agencyToken', token);

        // Convert to ISO string if it's a Date object, otherwise use as is
        const expiryString = expiresAt instanceof Date
          ? expiresAt.toISOString()
          : expiresAt;
        localStorage.setItem('agencyTokenExpiry', expiryString);

        // Convert to Date object for state storage
        const expiryDate = expiresAt instanceof Date
          ? expiresAt
          : new Date(expiresAt);

        set({
          token,
          expiresAt: expiryDate,
        });
      },
    }),
    {
      name: 'agency-auth-storage',
      partialize: (state) => ({
        agencyUser: state.agencyUser,
        isAuthenticated: state.isAuthenticated,
        token: state.token,
        expiresAt: state.expiresAt,
      }),
    }
  )
);
